import {List as GWCList, SlotSize, TooltipProps} from '@gwc/core'
import clsx from 'clsx'

import {Tooltip} from '../tooltip'
import {classes} from './List.classes'
import {useWrappedListItems} from './List.hooks'

export type ItemProps = {
  /** Класс, прокидываемый в List.Item */
  className?: string
  /** Текстовая метка содержания элемента для доступности */
  textValue?: string
  /** Отключает возможность выбора элемента */
  disabled?: boolean
  /** Вызывается при нажатии на элемент. Не путать с onChange, который обрабатывается на уровне списка - это событие вспомогательное, служит скорее для подключения всяких метрик и подобного. В большинстве случаев не нужно. */
  onPress?: VoidFunction
  /** Тултип, который будет показываться при наведении на элемент */
  tooltip?: Pick<TooltipProps, 'placement' | 'invert'> & {
    title: string
  }
}

export type ItemState = {
  isSelected: boolean
}

export type ListProps<T> = {
  /** ID заблокированных элементов, пропс из react-aria */
  disabledKeys?: string[]
  /** Размер элементов списка */
  size?: SlotSize
  /** Набор элементов списка */
  items: T[]
  /** Создает уникальный ключ для каждого элемента - нужно для оптимизации рендера списка, а также для определения того какой элемент выбран. Ключ должен быть уникальным */
  getId: (item: T) => string
  /** Задает каждому элементу соответствующие пропсы - см. интерфейс ItemProps */
  getItemProps?: (item: T) => ItemProps
  /** Выбранный элемент. Не обязательно должен быть ссылочно равен какому-то элементу из списка, главное чтобы они генерировали одинаковые ключи. */
  selectedItemId?: string
  /** Вызывается при изменении выбранного элемента */
  onItemChange?: (item?: T) => void
  /** Метка доступности для всего списка */
  'aria-label'?: string
  className?: string
  /** Вызывается для каждого элемента списка и возвращает, как тот должен выглядеть. Автоматически оборачивается в List.Item, возвращаться должно только содержание слота. **/
  renderItem: (item: T, state: ItemState) => JSX.Element
  noSelectedItemHighlight?: boolean
}
const itemPropsNoop = () => ({})

/** Компонент списка с интерактивными компонентами. Поддерживает только единичный выбор элемента. */
const List = Object.assign(
  function Listv2<T>({
    items,
    getId,
    selectedItemId,
    getItemProps = itemPropsNoop,
    noSelectedItemHighlight = false,
    onItemChange,
    renderItem,
    ...commonProps
  }: ListProps<T>) {
    const {wrappedItems, selection, onSelectionChange} = useWrappedListItems({
      items,
      getId,
      selectedItemId,
      onItemChange,
    })

    return (
      <GWCList
        {...commonProps}
        items={wrappedItems}
        onSelectionChange={onSelectionChange}
        selectedKeys={selection}
        selectionMode="single"
      >
        {({id, value}) => {
          const {textValue, tooltip, ...itemProps} = getItemProps(value)

          const listItem = (
            <GWCList.Item
              {...itemProps}
              className={clsx(itemProps.className, {[classes.noSelectedItemHighlight]: noSelectedItemHighlight})}
            >
              {({isSelected}) => renderItem(value, {isSelected})}
            </GWCList.Item>
          )

          return (
            <GWCList.CollectionItem key={id} textValue={textValue}>
              {tooltip !== undefined ? <Tooltip {...tooltip}>{listItem}</Tooltip> : listItem}
            </GWCList.CollectionItem>
          )
        }}
      </GWCList>
    )
  },
  {
    Label: GWCList.Label,
    Description: GWCList.Description,
    CheckTrail: GWCList.CheckTrail,
    RadioTrail: GWCList.RadioTrail,
  },
)

export default List
